import { useEffect, useMemo, useState } from "react";
import { Activity, TrendingUp, TrendingDown, Info } from "lucide-react";
import api from "@/lib/api";

/*
 * Encart « Prix de marché de gros » (spot day-ahead France, ENTSO-E).
 *
 * ⚠️  DONNÉE DISTINCTE DU TRV. Le comparateur utilise le TRV (prix de détail, €/kWh TTC).
 *     Ici c'est le prix SPOT de gros (€/MWh) : un CONTEXTE marché, pas un tarif client.
 *     Le disclaimer est affiché explicitement (exigence courtage).
 *
 * Consomme GET /api/market/spot/fr.
 */

const DISCLAIMER_FALLBACK =
  "Le prix spot est un prix de marché de gros. Il ne correspond pas au prix final payé par le consommateur.";

const nf = (v, dec) =>
  v == null || isNaN(v)
    ? "—"
    : Number(v).toLocaleString("fr-FR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

const parisDay = (iso) => new Date(iso).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" });
const parisHour = (iso) =>
  new Date(iso).toLocaleTimeString("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit" });

export default function MarketSpotCard() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error

  useEffect(() => {
    let on = true;
    api
      .get("/market/spot/fr")
      .then((r) => {
        if (!on) return;
        setData(r.data);
        setStatus("ok");
      })
      .catch(() => on && setStatus("error"));
    return () => {
      on = false;
    };
  }, []);

  const days = useMemo(() => {
    if (!data?.prices_hourly?.length) return [];
    const todayKey = new Date().toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" });
    const groups = new Map();
    for (const p of data.prices_hourly) {
      const key = parisDay(p.ts);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    }
    return [...groups.entries()].map(([key, points]) => ({
      key,
      label: key === todayKey ? "Aujourd'hui" : "Demain",
      points,
    }));
  }, [data]);

  if (status === "loading") {
    return <Shell><p className="text-sm text-primary-foreground/60">Chargement du prix de marché…</p></Shell>;
  }
  if (status === "error" || !data) {
    return (
      <Shell>
        <p className="text-sm text-primary-foreground/60">Prix de marché momentanément indisponible.</p>
      </Shell>
    );
  }

  const a = data.analysis || {};
  const chip =
    a.level === "low"
      ? { cls: "bg-accent/15 text-accent", Icon: TrendingDown }
      : a.level === "high"
      ? { cls: "bg-amber-400/15 text-amber-300", Icon: TrendingUp }
      : { cls: "bg-white/10 text-primary-foreground/70", Icon: Activity };

  return (
    <Shell>
      {/* En-tête : bien distinguer du tarif client */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-accent">
            Prix de marché de gros · France
          </div>
          <div className="text-[11px] text-primary-foreground/50">
            Spot day-ahead ENTSO-E · ≠ tarif client (TRV)
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${chip.cls}`}>
          <chip.Icon className="h-3.5 w-3.5" aria-hidden /> {a.label || "Marché"}
        </span>
      </div>

      {/* Prix courant : €/MWh canonique + €/kWh pédagogique */}
      <div className="flex items-end gap-6">
        <div>
          <div className="text-3xl font-extrabold text-white tabular-nums">
            {nf(data.current_price_mwh, 2)} €
          </div>
          <div className="text-xs text-primary-foreground/60">/ MWh — prix de gros</div>
        </div>
        <div>
          <div className="text-lg font-bold text-white/90 tabular-nums">
            {nf(data.current_price_kwh, 4)} €
          </div>
          <div className="text-xs text-primary-foreground/50">/ kWh (indicatif)</div>
        </div>
      </div>

      {/* Min / moyenne / max du jour */}
      {a.day_avg_mwh != null && (
        <div className="mt-3 flex gap-4 text-xs text-primary-foreground/60">
          <span>min {nf(a.day_min_mwh, 0)}</span>
          <span>moy {nf(a.day_avg_mwh, 0)}</span>
          <span>max {nf(a.day_max_mwh, 0)} €/MWh</span>
        </div>
      )}

      {/* Barres horaires aujourd'hui / demain */}
      <div className="mt-4 space-y-3">
        {days.map((d) => (
          <DayBars key={d.key} label={d.label} points={d.points} />
        ))}
      </div>

      {/* Source + mise à jour */}
      <div className="mt-4 flex justify-between text-[11px] text-primary-foreground/50">
        <span>Source : {data.source === "entsoe" ? "ENTSO-E" : data.source}</span>
        <span>Maj : {parisHour(data.retrieved_at)}</span>
      </div>

      {/* Disclaimer obligatoire */}
      <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-snug text-primary-foreground/45">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        {data.disclaimer || DISCLAIMER_FALLBACK}
      </p>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-primary p-5" aria-label="Prix de marché de gros">
      {children}
    </div>
  );
}

function DayBars({ label, points }) {
  const prices = points.map((p) => p.price_mwh);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const span = max - min || 1;
  const nowHour = new Date().toLocaleString("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit" });

  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold text-primary-foreground/60">{label}</div>
      <div className="flex items-end gap-[2px] h-12">
        {points.map((p) => {
          const h = 20 + ((p.price_mwh - min) / span) * 80; // 20%..100%
          const isNow =
            new Date(p.ts).toLocaleString("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit" }) === nowHour;
          return (
            <div
              key={p.ts}
              title={`${parisHour(p.ts)} · ${p.price_mwh} €/MWh`}
              className={`flex-1 rounded-sm ${isNow ? "bg-accent" : "bg-accent/35"}`}
              style={{ height: `${h}%` }}
            />
          );
        })}
      </div>
    </div>
  );
}
